import { Suspense, lazy, useEffect, useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { gsap } from "@/animations/gsap";
import { playHeroIntro } from "@/animations/hero";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useCanRender3D } from "@/hooks/useDevice";
import { hasWebGL } from "@/lib/webgl";
import { profile } from "@/data/profile";
import { scrollToSection } from "@/lib/scroll";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const HeroScene = lazy(() => import("./HeroScene"));

export function Hero({ started }: { started: boolean }) {
  const ref = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const can3D = useCanRender3D();
  const enable3D = can3D && !reduced && hasWebGL();

  useEffect(() => {
    if (!started || reduced) return undefined;
    const ctx = gsap.context(() => {
      playHeroIntro();
      gsap.to(contentRef.current, {
        yPercent: -12,
        opacity: 0.25,
        ease: "none",
        scrollTrigger: { trigger: ref.current, start: "top top", end: "bottom top", scrub: true },
      });
    }, ref);
    return () => ctx.revert();
  }, [started, reduced]);

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen items-center overflow-hidden" data-testid="hero">
      <div className="absolute inset-0" aria-hidden="true">
        {enable3D ? (
          <ErrorBoundary fallback={<div className="hero-fallback" />}>
            <Suspense fallback={<div className="hero-fallback" />}>
              <HeroScene />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <div className="hero-fallback" />
        )}
      </div>
      <div
        className="bg-grid pointer-events-none absolute inset-0 opacity-40"
        aria-hidden="true"
        style={{ maskImage: "radial-gradient(70% 70% at 50% 50%, black, transparent)" }}
      />

      <div ref={contentRef} className="container-x relative z-10 pb-24 pt-32">
        <p className="hero-eyebrow eyebrow">{profile.heroEyebrow}</p>
        <h1 className="font-display mt-8 text-[clamp(3.2rem,10vw,9.5rem)] font-semibold uppercase leading-[0.94] tracking-tight" data-testid="hero-heading">
          {profile.heroLines.map((line, i) => (
            <span className="mask-line" key={line}>
              <span className={`hero-line-inner ${i === 1 ? "text-outline" : ""}`}>
                {i === profile.heroLines.length - 1 ? (
                  <>
                    {line.slice(0, -1)}
                    <span style={{ color: "var(--accent)" }}>.</span>
                  </>
                ) : (
                  line
                )}
              </span>
            </span>
          ))}
        </h1>
        <div className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <p className="hero-sub max-w-md text-sm leading-relaxed text-muted md:text-base">{profile.heroSub}</p>
          <div className="flex flex-wrap gap-4">
            <button className="btn btn-primary hero-cta" onClick={() => scrollToSection("work")} data-testid="hero-cta-work">
              VIEW WORK
              <ArrowUpRight size={16} aria-hidden="true" />
            </button>
            <button className="btn btn-ghost hero-cta" onClick={() => scrollToSection("contact")} data-testid="hero-cta-connect">
              LET'S CONNECT
              <ArrowUpRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="hero-meta mt-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="scroll-line" aria-hidden="true" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-muted">SCROLL</span>
          </div>
          <p className="hidden font-mono text-[10px] tracking-[0.3em] text-muted md:block">{profile.heroFlow}</p>
        </div>
      </div>
    </section>
  );
}
